import { useMemo, useState } from 'react';
import { DataGrid, type ColumnDef } from './DataGrid';
import { Icon } from './Icon';
import { Status } from './Status';
import { useImports } from '@/hooks/useImports';
import { fmt, fmtBytes, formatDateTime, sourceLabel } from '@/lib/format';
import type { ImportFilters, ImportRow, ImportSource } from '@/api/imports';

type Props = {
  onOpen: (id: number) => void;
};

const STATUS_KINDS: Record<string, 'ok' | 'warn' | 'mute' | 'run'> = {
  SUCCEEDED: 'ok',
  PARTIAL: 'warn',
  FAILED: 'warn',
  RUNNING: 'run',
  PENDING: 'mute',
  CANCELLED: 'mute',
  ROLLING_BACK: 'run',
  ROLLED_BACK: 'mute',
};

const STATUS_LABELS: Record<string, string> = {
  SUCCEEDED: 'Terminé',
  PARTIAL: 'Partiel',
  FAILED: 'Échec',
  RUNNING: 'En cours',
  PENDING: 'En attente',
  CANCELLED: 'Annulé',
  ROLLING_BACK: 'Retrait en cours',
  ROLLED_BACK: 'Retiré',
};

/**
 * Sortable / filterable history of past imports using the shared
 * DataGrid wrapper. The source / status pickers stay above the grid
 * since they drive the server query; the grid then handles per-column
 * sorting, search, and pagination.
 */
export function ImportHistory({ onOpen }: Props) {
  const [source, setSource] = useState<ImportSource | ''>('');
  const [status, setStatus] = useState<NonNullable<ImportFilters['status']> | ''>('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });

  const query = useImports({
    source: source || undefined,
    status: status || undefined,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
  });
  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const columns = useMemo<ColumnDef<ImportRow>[]>(
    () => [
      {
        accessorFn: (r) => r.originalFilename ?? r.sourceRef,
        id: 'filename',
        header: 'Import',
        size: 280,
        Cell: ({ row }) => (
          <div className="cell-stack">
            <span className="pri" style={{ fontFamily: 'var(--mono)', fontSize: 12.5 }}>
              {row.original.originalFilename ?? row.original.sourceRef}
            </span>
            <span className="s2">
              #{row.original.id} · {fmtBytes(row.original.fileSizeBytes)}
            </span>
          </div>
        ),
      },
      {
        accessorFn: (r) => sourceLabel(r.source),
        id: 'source',
        header: 'Source',
        size: 130,
      },
      {
        accessorKey: 'itemsTotal',
        header: 'Total',
        size: 110,
        mantineTableHeadCellProps: { align: 'right' },
        mantineTableBodyCellProps: { align: 'right' },
        Cell: ({ cell }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink)' }}>
            {fmt(cell.getValue<number>())}
          </span>
        ),
      },
      {
        accessorKey: 'itemsAccepted',
        header: 'Acceptés',
        size: 110,
        mantineTableHeadCellProps: { align: 'right' },
        mantineTableBodyCellProps: { align: 'right' },
        Cell: ({ cell }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--accent-2)' }}>
            {fmt(cell.getValue<number>())}
          </span>
        ),
      },
      {
        accessorKey: 'itemsRejected',
        header: 'Rejetés',
        size: 110,
        mantineTableHeadCellProps: { align: 'right' },
        mantineTableBodyCellProps: { align: 'right' },
        Cell: ({ cell }) => {
          const v = cell.getValue<number>();
          return (
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 700,
                color: v > 0 ? 'var(--alert-ink)' : 'var(--ink-3)',
              }}
            >
              {fmt(v)}
            </span>
          );
        },
      },
      {
        accessorFn: (r) => r.triggeredBy ?? '',
        id: 'triggeredBy',
        header: 'Opérateur',
        size: 180,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
            {cell.getValue<string>() || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'startedAt',
        header: 'Démarré',
        size: 170,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)' }}>
            {formatDateTime(cell.getValue<string>())}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        size: 130,
        Cell: ({ cell }) => {
          const s = cell.getValue<string>();
          return (
            <Status kind={STATUS_KINDS[s] ?? 'mute'}>{STATUS_LABELS[s] ?? s}</Status>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="fade-in">
      <div className="toolbar">
        <label className="input">
          <Icon name="layers" />
          <select value={source} onChange={(e) => setSource((e.target.value as ImportSource) || '')}>
            <option value="">Toutes les sources</option>
            <option value="ORANGE">Orange</option>
            <option value="ID30">PDE.CI</option>
            <option value="CALL_CENTER">Call center</option>
            <option value="PARTNER_BATCH">Lot partenaire</option>
          </select>
        </label>
        <label className="input">
          <Icon name="filter" />
          <select
            value={status}
            onChange={(e) => setStatus((e.target.value as ImportFilters['status']) || '')}
          >
            <option value="">Tout statut</option>
            <option value="SUCCEEDED">Terminé</option>
            <option value="PARTIAL">Partiel</option>
            <option value="FAILED">Échec</option>
            <option value="RUNNING">En cours</option>
            <option value="PENDING">En attente</option>
            <option value="CANCELLED">Annulé</option>
            <option value="ROLLED_BACK">Retiré</option>
          </select>
        </label>
      </div>

      <DataGrid
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        rowCount={total}
        onRowClick={(r) => onOpen(r.id)}
        emptyMessage="Aucun import. Lancez votre premier import depuis « Importer un fichier »."
      />
    </div>
  );
}
